import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Thermometer, Snowflake, Sun, ShoppingBasket } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = 'hg_vitals_cache';

const FROST_TONE = { none: '#4E7A33', low: '#8FB4D6', medium: '#C9A85B', high: '#C66B4A', unknown: '#5C665C' };
const UV_TONE = { low: '#4E7A33', moderate: '#C9A85B', high: '#C66B4A', very_high: '#C66B4A', extreme: '#B23A2E' };

// Pinned horizontal "Garden Intelligence HUD" — live tickers for the desktop canvas.
export default function VitalsHud() {
  const [v, setV] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(CACHE_KEY)) || null; } catch { return null; }
  });

  useEffect(() => {
    axios.get(`${API}/ai/coach-vitals`, { withCredentials: true })
      .then((r) => {
        setV(r.data);
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(r.data)); } catch { /* ignore */ }
      })
      .catch(() => {});
  }, []);

  const soil = v?.soil_temp;
  const frost = v?.frost_risk;
  const uv = v?.uv;
  const nh = v?.next_harvest;
  const frostAlert = frost?.available && (frost.level === 'high' || frost.level === 'medium');

  const items = [
    { key: 'soil', Icon: Thermometer, label: 'Soil Temp', tone: '#1A4331',
      value: soil?.available ? `${soil.value}°C` : '—', alert: false, testId: 'vitals-soil-temp' },
    { key: 'frost', Icon: Snowflake, label: frost?.available ? frost.label : 'Frost Risk', tone: FROST_TONE[frost?.level] || '#5C665C',
      value: frost?.available ? (frost.tonight_min != null ? `${frost.tonight_min}°C` : frost.label) : '—', alert: !!frostAlert, testId: 'vitals-frost-risk' },
    { key: 'uv', Icon: Sun, label: uv?.available ? `UV ${uv.label}` : 'UV Index', tone: UV_TONE[uv?.level] || '#5C665C',
      value: uv?.available ? `${uv.value}` : '—', alert: false, testId: 'vitals-uv' },
    { key: 'harvest', Icon: ShoppingBasket, label: nh ? 'Next Harvest' : 'No crops', tone: '#4E7A33',
      value: nh ? (nh.ready ? 'Now' : `${nh.days_away}d`) : '—', alert: false, testId: 'vitals-next-harvest' },
  ];

  return (
    <div data-testid="vitals-hud" className="hg-glass rounded-2xl px-3 sm:px-5 py-3 mb-7 flex items-center gap-1 sm:gap-3 overflow-x-auto hg-scroll shadow-[0_8px_30px_-14px_rgba(26,67,49,0.35)]">
      <div className="hidden xl:flex flex-col justify-center pr-4 mr-1 border-r border-[#1A4331]/10 flex-shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#7A6B57] leading-tight">Garden<br />Intelligence</span>
      </div>
      {items.map((it) => {
        const Icon = it.Icon;
        return (
          <div key={it.key} data-testid={it.testId} className="flex items-center gap-3 px-3 py-1 flex-shrink-0">
            <div className="relative w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${it.tone}18` }}>
              <Icon className="w-[18px] h-[18px]" style={{ color: it.tone }} />
              {it.alert ? <span className="hg-pulse-dot absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full" style={{ background: it.tone }} /> : null}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#7A6B57] leading-none whitespace-nowrap">{it.label}</p>
              <p className="text-lg font-bold leading-tight whitespace-nowrap" style={{ color: it.tone, fontFamily: 'Outfit, sans-serif' }}>{it.value}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
